// Листы перед текстом (в порядке СТО): состав работы, титул (title.tsx), задание, лист для замечаний, реферат /
// аннотация (шапка со сведениями об объёме и ключевыми словами - как у writer), живое оглавление. А также
// вставки в текст: где встанет раздел об ИИ (3.9) и автоматический список источников.
import { useEffect, useRef, useState } from 'react';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Ctx } from '../../core/registry';
import { useApp } from '../../core/store';
import { MiniEditor } from '../../core/editor/MiniEditor';
import { assetUrl } from '../../core/schema/objects';
import { useNumbering, numberingStore, numberingKey } from '../../core/editor/numberingPlugin';
import type { Numbering } from '../../core/numbering';
import { gotoNode } from '../../core/docIndex';
import { ctx as gctx } from '../../core/ctx';
import { appStore } from '../../core/store';
import { Icon } from '../../ui/icons';
import { useUi } from '../../ui/uiState';
import { NbspExtension } from '../pages/nbsp';
import { FrontPagesExtension } from '../pages/front';
import { TypographyExtension } from './typography';
import { usePages } from '../pages/store';
import { typoText, nbspParts } from '../pages/nbsp';

/** Расширения малого редактора на листе в «Разметке страницы»: неразрывные пробелы как в DOCX (только при USE_NBSP) и перенос текста на
 *  следующий лист A4 (features/pages/front.ts). */
const SHEET_EXT = [NbspExtension, FrontPagesExtension, TypographyExtension];
/** Черновик (без разметки страниц): типографика на лету - как в основном тексте (в DOCX writer поставит то же). */
const SHEET_EXT_DRAFT = [TypographyExtension];
import { ELEMENTS, elementNeed, wt, authorInitials, coauthorList, signName, keywordCase, ruPlural, capFirst, richText, NB, EN,
  zadanieTitle, zadanieTitleOptions, zadanieForm, TASK_FORM_TYPES, tocOn, tocRequired } from './data';

// ------------------------------------------------------------------------------------------------ состав работы
export function FrontBar({ ctx }: { ctx: Ctx }) {
  const m = useApp((s) => s.meta);
  if (!m) return null;
  const setMeta = ctx.store.getState().setMeta;
  const items: { id: string; title: string; on: boolean; toggle?: (v: boolean) => void; need: string }[] = [
    { id: 'title', title: 'Титульный лист', on: true, need: '+' },
    { id: 'zadanie', title: zadanieTitle(m), on: m.zadanie.enabled, need: elementNeed(ELEMENTS[0], m.type),
      toggle: (v) => setMeta((x) => ({ ...x, zadanie: { ...x.zadanie, enabled: v } })) },
    { id: 'remarks', title: 'Лист для замечаний', on: m.remarks_page, need: elementNeed(ELEMENTS[1], m.type), toggle: (v) => setMeta((x) => ({ ...x, remarks_page: v })) },
    { id: 'referat', title: m.annotation.enabled ? 'Аннотация' : 'Реферат', on: m.referat.enabled || m.annotation.enabled, need: elementNeed(ELEMENTS[2], m.type),
      toggle: (v) => setMeta((x) => ({ ...x, referat: { ...x.referat, enabled: v && !x.annotation.enabled }, annotation: { ...x.annotation, enabled: v && x.annotation.enabled } })) },
    { id: 'toc', title: 'Оглавление', on: tocOn(m), need: elementNeed(ELEMENTS[3], m.type),
      toggle: tocRequired(m) ? undefined : (v) => setMeta((x) => ({ ...x, toc_page: v })) },
    { id: 'ai', title: 'Раздел об ИИ', on: m.ai_usage.enabled, need: '', toggle: (v) => setMeta((x) => ({ ...x, ai_usage: { ...x.ai_usage, enabled: v } })) },
  ];
  return (
    <div className="front-bar" data-testid="front-bar">
      <span className="front-bar-title">{wt(m.type).name}{m.number ? ` № ${m.number}` : ''} · состав по СТО:</span>
      {items.map((it) => {
        const need = (it.need === '+' ? 'Обязательно' : it.need === 'р' ? 'Рекомендуется' : 'По желанию') + ' для этого вида работы (табл. 1 СТО)';
        // щелчок по названию - перейти к листу (выключенный - сначала включить); убрать лист - отдельной кнопкой «×»
        const go = () => {
          if (!it.on && it.toggle) it.toggle(true);
          window.setTimeout(() => {
            if (it.id === 'ai') { const w = document.querySelector('[data-testid=ai-widget]') as HTMLElement | null; if (w) w.scrollIntoView({ block: 'start' }); else ctx.openDialog('sto.meta', { tab: 'ai' }); return; }
            const el = document.querySelector(`[data-sheet="${it.id}"]`) as HTMLElement | null;
            const cv = document.querySelector('.canvas') as HTMLElement | null;
            if (el && cv) cv.scrollTop += el.getBoundingClientRect().top - cv.getBoundingClientRect().top - 16;
          }, it.on ? 0 : 120);
        };
        return (
          <span key={it.id} className={'front-chip' + (it.on ? ' is-on' : '') + (!it.toggle ? ' is-fixed' : '')} data-testid={'front-' + it.id}>
            <button type="button" className="front-chip-go" title={need + (it.on ? ' · щелчок - перейти к листу' : ' · щелчок - добавить лист')} onClick={go}>
              <Icon name={it.on ? 'ok' : 'plus'} size={12} /> {it.title}{it.need && <sup className={'need-' + (it.need === '+' ? 'req' : 'rec')}>{it.need}</sup>}
            </button>
            {it.toggle && it.on && (
              <button type="button" className="front-chip-off" title={`Убрать «${it.title}» из работы (текст сохранится - можно вернуть)`}
                onClick={() => { it.toggle!(false); ctx.toast(`«${it.title}» убран из работы - вернуть: щелчок по «+ ${it.title}»`, 'info', 4000); }}
                data-testid={'front-off-' + it.id}>×</button>
            )}
          </span>
        );
      })}
      <button type="button" className="front-more" onClick={() => ctx.openDialog('sto.meta', { tab: 'front' })}>Настроить…</button>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ текст на листе
/** Статичный показ форматированного фрагмента (абзацы и перечисления) - без редактора, пока не нужен. */
function RichView({ doc }: { doc: any }) {
  const inline = (nodes: any[] = []) => nodes.map((n, i) => {
    if (n.type === 'text') {
      let el: any = n.text;
      for (const m of n.marks || []) {
        if (m.type === 'bold') el = <b key={i}>{el}</b>;
        else if (m.type === 'italic') el = <i key={i}>{el}</i>;
        else if (m.type === 'underline') el = <u key={i}>{el}</u>;
        else if (m.type === 'superscript') el = <sup key={i}>{el}</sup>;
        else if (m.type === 'subscript') el = <sub key={i}>{el}</sub>;
        else if (m.type === 'code') el = <code key={i}>{el}</code>;
      }
      return <span key={i}>{el}</span>;
    }
    if (n.type === 'mathInline') return <code key={i}>{n.attrs?.latex}</code>;
    if (n.type === 'hardBreak') return <br key={i} />;
    if (n.type === 'inlineImage' && n.attrs?.src) {
      return <img key={i} className="sto-inline-image" src={assetUrl(n.attrs.src)} alt={n.attrs?.alt || ''}
        style={{ width: /^\d+(\.\d+)?cm$/.test(String(n.attrs?.width || '')) ? n.attrs.width : undefined, maxWidth: '100%', verticalAlign: 'middle' }} />;
    }
    return null;
  });
  const capOf = (n: any) => inline((n.content || []).find((c: any) => c.type === 'caption')?.content || []);
  const block = (n: any, i: number): any => {
    if (n.type === 'paragraph') return <p key={i}>{inline(n.content)}</p>;
    if (n.type === 'stoList') {
      const Tag = n.attrs?.kind === 'digit' || n.attrs?.kind === 'letter' ? 'ol' : 'ul';
      return <Tag key={i} className="sto-list" data-kind={n.attrs?.kind || 'auto'}>
        {(n.content || []).map((li: any, j: number) => <li key={j}>{(li.content || []).map(block)}</li>)}</Tag>;
    }
    // объекты листа (схема цепи, таблица исходных данных, формула, код) - так же, как в Word, без правки
    if (n.type === 'figure') {
      return <figure key={i} className="sto-figure">
        <div className="sto-figure-img">{n.attrs?.src ? <img src={assetUrl(n.attrs.src)} alt={n.attrs?.alt || ''} style={{ maxWidth: '100%' }} /> : <span>нет изображения</span>}</div>
        <div className="sto-figure-cap">{capOf(n)}</div></figure>;
    }
    if (n.type === 'stoTable') {
      const t = (n.content || []).find((c: any) => c.type === 'table');
      return <div key={i} className="sto-table"><div className="sto-caption">{capOf(n)}</div>
        <table><tbody>{(t?.content || []).map((tr: any, r: number) => <tr key={r}>{(tr.content || []).map((td: any, c: number) => {
          const Cell = td.type === 'tableHeader' ? 'th' : 'td';
          return <Cell key={c} colSpan={td.attrs?.colspan || 1} rowSpan={td.attrs?.rowspan || 1}>{(td.content || []).map(block)}</Cell>;
        })}</tr>)}</tbody></table></div>;
    }
    if (n.type === 'formula') return <p key={i} className="sto-formula"><code>{n.attrs?.latex}</code></p>;
    if (n.type === 'listing') return <div key={i} className="sto-listing"><div className="sto-caption">{capOf(n)}</div><pre>{n.attrs?.code}</pre></div>;
    return null;
  };
  return <>{(doc?.content || []).map(block)}</>;
}

/** Редактор фрагмента на листе: до щелчка - статичный текст (на странице нет лишних редакторов), по щелчку -
 *  малый редактор с курсором в конце; уход фокуса - снова статичный текст. */
function SheetEditor({ value, onChange, placeholder, testid, minHeight, where }: {
  value: any; onChange: (v: any) => void; placeholder: string; testid: string; minHeight: number; where: string;
}) {
  const [active0, setActive] = useState(false);
  const pagesMode = useUi((s) => s.showPages);
  // в «Разметке страницы» - всегда редактор: текст на листе разбит на страницы так же, как в DOCX
  const active = active0 || pagesMode;
  const wrap = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!active0) return;
    const t = window.setTimeout(() => {
      const el = wrap.current?.querySelector('.ProseMirror') as HTMLElement | null;
      if (!el) return;
      el.focus();
      const r = document.createRange();
      r.selectNodeContents(el);
      r.collapse(false);
      const s = window.getSelection();
      s?.removeAllRanges();
      s?.addRange(r);
    }, 0);
    return () => window.clearTimeout(t);
  }, [active0]);
  const empty = !richText(value).trim() && !(value?.content || []).some((n: any) => ['figure', 'stoTable', 'formula', 'listing'].includes(n.type));
  if (!active) {
    return (
      <div className="mini-editor-wrap sheet-rich" style={{ minHeight }} tabIndex={0} data-testid={testid} title="Щёлкните, чтобы писать"
        onClick={() => setActive(true)} onFocus={() => setActive(true)}>
        <div className="mini-editor sto-doc">{empty ? <p className="sheet-rich-ph">{placeholder}</p> : <RichView doc={value} />}</div>
      </div>
    );
  }
  return (
    <div ref={wrap} onBlur={(e) => { if (!wrap.current?.contains(e.relatedTarget as Node)) setActive(false); }}>
      <MiniEditor value={value} onChange={onChange} placeholder={placeholder} testid={testid} minHeight={minHeight} paper={false} where={where}
        extensions={pagesMode ? SHEET_EXT : SHEET_EXT_DRAFT} key={pagesMode ? 'pg' : 'draft'} objects />
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ задание
export function ZadanieSheet({ ctx }: { ctx: Ctx }) {
  const m = useApp((s) => s.meta);
  const pid = useApp((s) => s.projectId);
  if (!m || !m.zadanie.enabled) return null;
  const form = zadanieForm(m);
  const canForm = TASK_FORM_TYPES.includes(m.type);
  return (
    <div className="front-page" data-testid="zadanie-sheet">
      <select className="front-page-title as-title" value={zadanieTitle(m)} title="Заголовок листа"
        onChange={(e) => ctx.store.getState().setMeta((x) => ({ ...x, zadanie: { ...x.zadanie, title: e.target.value } }))}>
        {/* просто «Задание» у лабораторной и т. п. экспорт выводит как «Задание на лабораторную работу» (zadanieTitle);
            название из загруженного Word - тоже в списке */}
        {zadanieTitleOptions(m).map((t) => <option key={t}>{t}</option>)}
      </select>
      <SheetEditor key={pid || ''} value={m.zadanie.doc} testid="zadanie-editor" where="Задание"
        placeholder={form
          ? 'Строки бланка: «Кафедра …», «Тема: …», «Исходные данные: …», «Основные разделы работы с указанием вопросов, подлежащих рассмотрению: …», «Срок выполнения: с «__» ____ 20__ г. по «__» ____ 20__ г.», «Руководитель …»'
          : 'Текст задания: цель работы, что нужно сделать (перечисление а), б), в)…)'}
        onChange={(doc) => ctx.store.getState().setMeta((x) => ({ ...x, zadanie: { ...x.zadanie, doc } }))} minHeight={120} />
      <div className="sheet-hint" data-testid="zadanie-hint">{form
        ? 'Задание (3.1) · в DOCX - бланк по форме Прил. Б СТО: поля - из строк «Кафедра …», «по …», «Тема: …», «Исходные данные: …», «Срок выполнения: с … по …», «Руководитель …», недостающие - из данных титула (журнал сборки)'
        : canForm ? 'Задание (3.1) · отдельный лист после титула: заголовок и текст как набраны, без бланка'
          : 'Задание (3.1) · отдельный лист после титула'}
        {canForm && (
          <label className="check sheet-hint-check" title="Снимите, если преподаватель просит лист задания без бланка - заголовком и текстом">
            <input type="checkbox" checked={form} data-testid="zadanie-form"
              onChange={(e) => ctx.store.getState().setMeta((x) => ({ ...x, zadanie: { ...x.zadanie, form: e.target.checked } }))} />
            {' '}бланком по форме Прил. Б
          </label>
        )}</div>
    </div>
  );
}

export function RemarksSheet() {
  const m = useApp((s) => s.meta);
  if (!m || !m.remarks_page) return null;
  return (
    <div className="front-page front-remarks" data-testid="remarks-sheet">
      <div className="front-page-title">Лист для замечаний</div>
      <div className="remarks-blank muted">Пустой лист для замечаний руководителя</div>
      <div className="sheet-hint">Лист для замечаний · отдельная страница</div>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ реферат
export function ReferatSheet({ ctx }: { ctx: Ctx }) {
  const m = useApp((s) => s.meta);
  const pid = useApp((s) => s.projectId);
  const checkPages = useApp((s) => s.check.result?.pages);
  // число страниц - из «Разметки страницы» (как поле NUMPAGES в DOCX), иначе - из последней сборки
  const layoutPages = usePages((s) => (s.ready ? s.frontCount + s.main.length : 0));
  const pages = layoutPages || checkPages;
  const nb = useNumbering();
  if (!m || !(m.referat.enabled || m.annotation.enabled)) return null;
  const isRef = m.referat.enabled;
  const key = isRef ? 'referat' : 'annotation';
  const w = wt(m.type);
  const c = nb.counts;
  const srcN = nb.sourceList.length;
  const vol = [`${pages ?? '…'}${NB}с.`];
  if (c.figures) vol.push(`${c.figures}${NB}рис.`);
  if (c.tables) vol.push(`${c.tables}${NB}табл.`);
  if (srcN) vol.push(`${srcN}${NB}${ruPlural(srcN, ['источник', 'источника', 'источников'])}`);
  if (c.appendices) vol.push(`${c.appendices}${NB}прил.`);
  const kw = m.keywords.map(keywordCase);
  // как writer emit_referat_head: точка в конце - только если хвост ещё не кончается точкой («2 прил.»)
  const volText = vol.join(', ');
  const shortName = (m.student.student_short || (() => {
    const p = String(m.student.student || '').split(/\s+/).filter(Boolean);
    return p.length >= 3 ? `${p[1][0]}. ${p[2][0]}. ${p[0]}` : '';
  })()).replace(/ /g, NB);
  return (
    <div className="front-page" data-testid="referat-sheet">
      <div className="front-page-title">{isRef ? 'Реферат' : 'Аннотация'}</div>
      {isRef && (
        <div className="referat-head" title="Эти строки writer составит сам из данных работы (3.2.3)">
          <p>{[m.student.student, ...coauthorList(m.student)].filter(Boolean).map(authorInitials).join(', ')} {m.title ? capFirst(m.title.replace(/\.$/, '')) + '.' : <span className="ts-empty">[тема работы]</span>}</p>
          <p>Руководитель {EN} {m.teacher.name || <span className="ts-empty">[руководитель]</span>}{m.teacher.position ? ', ' + m.teacher.position.replace(/\.$/, '') : ''}.</p>
          <p>{w.name}: текстовый документ объёмом {volText}{volText.endsWith('.') ? '' : '.'}</p>
          <p className={kw.length < 5 || kw.length > 15 ? 'kw-bad' : ''}>Ключевые слова: {kw.length ? kw.join(', ') + '.' : <span className="ts-empty">5–15 слов</span>}</p>
          {/* правка - на поле листа, вне строк текста (не сдвигает текст реферата) */}
          <button type="button" className="link-btn small referat-edit" onClick={() => ctx.openDialog('sto.meta', { tab: 'work', field: 'keywords' })}
            data-testid="referat-edit">изменить</button>
        </div>
      )}
      <SheetEditor key={(pid || '') + key} value={m[key].doc} testid="referat-editor" where={isRef ? 'Реферат' : 'Аннотация'}
        placeholder={isRef ? 'Объект исследования, цель работы, методы, полученные результаты, область применения…' : 'Краткая характеристика работы…'}
        onChange={(doc) => ctx.store.getState().setMeta((x) => ({ ...x, [key]: { ...x[key], doc } }))} minHeight={100} />
      {isRef && <p className="referat-sign pg-keep">Дата ______________ Подпись ______________ {shortName}</p>}
      {/* групповая работа: подпись каждого соавтора своей строкой (как writer emit_referat_sign) */}
      {isRef && coauthorList(m.student).map((co, i) => (
        <p key={i} className="referat-sign pg-keep">Дата ______________ Подпись ______________ {signName(co)}</p>
      ))}
      <div className="sheet-hint">{isRef ? 'Реферат (3.2.3): шапка и подпись - автоматически' : 'Аннотация (3.2)'}</div>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ оглавление
export function TocSheet({ ctx }: { ctx: Ctx }) {
  const nb = useNumbering();
  const m = useApp((s) => s.meta);
  if (!m || !tocOn(m)) return null;
  const rows = nb.order.map((id) => [id, nb.byId.get(id)!] as const)
    .filter(([, i]) => i && ((i.kind === 'sec' && (i.level || 1) <= 2 && i.num) || i.kind === 'struct' || i.kind === 'app'));
  return (
    <div className="front-page front-toc" data-testid="toc-sheet">
      <div className="front-page-title">Оглавление</div>
      {!rows.length && <div className="muted">Оглавление появится, когда в тексте будут заголовки.</div>}
      {rows.map(([id, i]) => (
        <button key={id} type="button" className={'toc-row lvl-' + (i.kind === 'sec' ? i.level : 1)}
          onClick={() => (id === 'meta_ai' ? ctx.openDialog('sto.meta', { tab: 'ai' }) : ctx.editor && gotoNode(ctx.editor, id))}>
          <span className="toc-text">{i.kind === 'app' ? `${i.label.replace(NB, ' ')} (${i.status}) ${i.title}` : i.kind === 'sec' ? `${i.num} ${i.title}` : i.title}</span>
          <span className="toc-dots" /><span className="toc-page">…</span>
        </button>
      ))}
      <div className="sheet-hint">Оглавление строится автоматически (поле Word); щелчок - перейти</div>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------ вставки в текст
const widgetKey = new PluginKey('stoFrontWidgets');

/** Форматированный фрагмент (абзацы и перечисления) -> DOM с классами основного текста (оформление как в DOCX). */
function richDom(doc: any, into: HTMLElement) {
  const inline = (p: HTMLElement, nodes: any[] = []) => {
    for (const n of nodes) {
      if (n.type === 'text') {
        let el: Node = document.createTextNode(n.text || '');
        for (const m of n.marks || []) {
          const tag = ({ bold: 'b', italic: 'i', underline: 'u', superscript: 'sup', subscript: 'sub', code: 'code' } as Record<string, string>)[m.type];
          if (!tag) continue;
          const w = document.createElement(tag);
          w.appendChild(el);
          el = w;
        }
        p.appendChild(el);
      } else if (n.type === 'hardBreak') p.appendChild(document.createElement('br'));
      else if (n.type === 'mathInline') { const c = document.createElement('code'); c.textContent = n.attrs?.latex || ''; p.appendChild(c); }
    }
  };
  const block = (n: any, parent: HTMLElement) => {
    if (n.type === 'paragraph') { const p = document.createElement('p'); inline(p, n.content); parent.appendChild(p); }
    else if (n.type === 'stoList') {
      const l = document.createElement(n.attrs?.kind === 'digit' || n.attrs?.kind === 'letter' ? 'ol' : 'ul');
      l.className = 'sto-list';
      l.setAttribute('data-kind', n.attrs?.kind || 'auto');
      for (const li of n.content || []) { const e = document.createElement('li'); (li.content || []).forEach((c: any) => block(c, e)); l.appendChild(e); }
      parent.appendChild(l);
    }
  };
  (doc?.content || []).forEach((n: any) => block(n, into));
}

/** Раздел об ИИ (3.9) в тексте - так, как его соберёт writer: заголовок раздела с номером, подразделы «N.1», «N.2»
 *  и текст с оформлением основного текста. Правка - в окне «Данные работы → ИИ» (щелчок по разделу). */
function aiWidget(num: string): HTMLElement {
  const m = appStore.getState().meta!;
  const el = document.createElement('div');
  el.className = 'sto-ai-widget';
  el.contentEditable = 'false';
  el.setAttribute('data-testid', 'ai-widget');
  el.title = 'Раздел об ИИ (п. 3.9) - щелчок, чтобы изменить';
  const h = document.createElement('h1');
  h.className = 'w-head';
  el.appendChild(h);
  const subs: HTMLElement[] = [];
  const parts: [string, any][] = [['Фактологическое описание использования ИИ', m.ai_usage.facts], ['Резюме-рефлексия', m.ai_usage.reflection]];
  let k = 0;
  for (const [title, doc] of parts) {
    if (!richText(doc).trim()) continue;
    k++;
    const h2 = document.createElement('h2');
    h2.className = 'w-sub';
    h2.dataset.title = title;
    h2.dataset.k = String(k);
    subs.push(h2);
    el.appendChild(h2);
    const body = document.createElement('div');
    body.className = 'w-body';
    richDom(doc, body);
    el.appendChild(body);
  }
  if (!k) {
    const p = document.createElement('p');
    p.className = 'w-empty';
    p.textContent = 'Опишите, какие системы ИИ и для чего применялись, - щёлкните здесь';
    el.appendChild(p);
  }
  // номер раздела - из сервиса нумерации (при первой отрисовке он может быть ещё не посчитан - обновляется сам)
  const setNum = (n: string) => {
    h.textContent = `${n ? n + ' ' : ''}Информация об использовании технологий искусственного интеллекта (ИИ)`;
    for (const x of subs) x.textContent = `${n ? n + '.' + x.dataset.k + ' ' : ''}${x.dataset.title}`;
  };
  setNum(num || numberingStore.get().byId.get('meta_ai')?.num || '');
  let seen = false;
  const unsub = numberingStore.subscribe(() => {
    if (el.isConnected) seen = true;
    else if (seen) { unsub(); return; }
    setNum(numberingStore.get().byId.get('meta_ai')?.num || '');
  });
  const foot = document.createElement('div');
  foot.className = 'w-foot';
  foot.innerHTML = '<a href="#" data-act="edit">изменить</a> · <a href="#" data-act="off">убрать раздел</a>';
  el.appendChild(foot);
  el.addEventListener('mousedown', (e) => e.preventDefault());
  el.addEventListener('click', (e) => {
    const act = (e.target as HTMLElement).getAttribute?.('data-act');
    e.preventDefault();
    if (act === 'off') appStore.getState().setMeta((x) => ({ ...x, ai_usage: { ...x.ai_usage, enabled: false } }));
    else void gctx.openDialog('sto.meta', { tab: 'ai' });
  });
  return el;
}

function sourcesWidget(nb: Numbering): HTMLElement {
  const el = document.createElement('div');
  el.className = 'sto-auto-sources';
  el.contentEditable = 'false';
  const h = document.createElement('div');
  h.className = 'w-head';
  h.textContent = 'Список использованных источников';
  el.appendChild(h);
  nb.sourceList.forEach(([, text], i) => {
    const p = document.createElement('p');
    p.className = 'sto-source-item';
    for (const x of nbspParts(`${i + 1} ${typoText(text)}`)) {
      if (!x.nb) { p.appendChild(document.createTextNode(x.text)); continue; }
      const sp = document.createElement('span');
      sp.className = 'pg-nb';
      sp.textContent = x.text;
      p.appendChild(sp);
    }
    el.appendChild(p);
  });
  const f = document.createElement('div');
  f.className = 'w-foot';
  f.textContent = 'Добавлен автоматически из библиотеки источников (4.13)';
  el.appendChild(f);
  el.addEventListener('mousedown', (e) => e.preventDefault());
  return el;
}

/** Где writer вставит раздел об ИИ: перед первым из «Выводы/Заключение/Список источников/приложение». */
function aiPos(doc: PMNode): number {
  let pos = -1;
  doc.forEach((n, off) => {
    if (pos >= 0) return;
    if (n.type.name === 'appendix' || (n.type.name === 'structHeading' && ['conclusions', 'conclusion', 'sources'].includes(n.attrs.kind))) pos = off;
  });
  return pos >= 0 ? pos : doc.content.size;
}

function widgets(doc: PMNode, nb: Numbering): DecorationSet {
  const m = appStore.getState().meta;
  const decos: Decoration[] = [];
  if (m?.ai_usage?.enabled) {
    const num = nb.byId.get('meta_ai')?.num || '';
    const key = 'ai:' + JSON.stringify([m.ai_usage.facts, m.ai_usage.reflection]).length + ':' + richText(m.ai_usage.facts).slice(0, 200) + richText(m.ai_usage.reflection).slice(-100) + ':' + num;
    decos.push(Decoration.widget(aiPos(doc), () => aiWidget(num), { side: -1, key, ignoreSelection: true }));
  }
  const hasSrc = (() => { let f = false; doc.forEach((n) => { if (n.type.name === 'structHeading' && n.attrs.kind === 'sources') f = true; }); return f; })();
  if (!hasSrc && nb.sourceList.length) {
    let pos = doc.content.size;
    doc.forEach((n, off) => { if (pos === doc.content.size && n.type.name === 'appendix') pos = off; });
    decos.push(Decoration.widget(pos, () => sourcesWidget(nb), { side: -1, key: 'src:' + nb.sourceList.map((x) => x[1]).join('|').length + ':' + nb.sourceList.length, ignoreSelection: true }));
  }
  return DecorationSet.create(doc, decos);
}

export const FrontWidgetsExtension = Extension.create({
  name: 'stoFrontWidgets',
  addProseMirrorPlugins() {
    return [new Plugin({
      key: widgetKey,
      state: {
        init: (_, state) => widgets(state.doc, numberingKey.getState(state)?.nb || numberingStore.get()),
        apply: (tr, prev, _o, state) => (tr.docChanged || tr.getMeta(numberingKey) !== undefined || tr.getMeta(widgetKey)
          ? widgets(state.doc, numberingKey.getState(state)?.nb || numberingStore.get()) : prev),
      },
      props: { decorations: (state) => widgetKey.getState(state) },
    })];
  },
});

/** Перерисовать вставки (после изменения метаданных/источников). */
export function refreshWidgets() {
  const ed = appStore.getState().editor;
  if (ed && !ed.isDestroyed) ed.view.dispatch(ed.state.tr.setMeta(widgetKey, true).setMeta('addToHistory', false));
}
